import { JSONApi } from './jsonapi.js';

/**
 * Find all corresponding relationship items
 *
 * @param {Object} response - JSON:API styled response from the server.
 * @param {Object} temp - Relationship data field
 * @throws {Error} A data relationship item does not contain type field.
 * @throws {Error} A data relationship item does not contain id field.
 * @return {Object} result - Included items grouped by their type
 */
export function findItem<T>(
  response: JSONApi.Response<T>,
  resource: JSONApi.Resource<T> | null,
  relationship: JSONApi.RelationshipResource
) {
  // Checking if temp has type field
  if (!relationship.hasOwnProperty('type')) {
    throw new Error(
      '[Normalize] [JSON:API Syntax Error] A data relationship item does not contain type field!'
    );
  }
  // Checking if temp has id field
  if (!relationship.hasOwnProperty('id')) {
    throw new Error(
      '[Normalize] [JSON:API Syntax Error] A data relationship item does not contain id field!'
    );
  }

  return (
    (response.included ?? []).find(
      (item: any) =>
        // Reflexive relationships, fix infinity loop
        resource.type !== String(item.type) &&
        String(item.id) === String(relationship.id) &&
        String(item.type) === String(relationship.type)
    ) ?? relationship
  );
}

/**
 * Validating data field from the server response
 *
 * @param {Object} response - JSON:API styled response from the server.
 * @throws {Error} Not all data items are the same type.
 * @throws {Error} A data item does not contain type field.
 * @throws {Error} The data field must be an object or array.
 * @throws {Error} A data field does not exist.
 */
export function validate<T>(response: JSONApi.Response<T>) {
  // Checking if there is data filed in the response
  if (!response.hasOwnProperty('data')) {
    throw new Error('[Normalize] [JSON:API Syntax Error] A data field does not exist!');
  }
  if (Array.isArray(response.data) && response.data.length === 0) {
    return true;
  }
  // Checking the type of data
  if (response.data.constructor !== Object && response.data.constructor !== Array) {
    throw new Error(
      '[Normalize] [JSON:API Syntax Error] The data field must be an object or array!'
    );
  }

  // Checking if the first item has type field
  if (Array.isArray(response.data) && !response.data[0].hasOwnProperty('type')) {
    throw new Error('[Normalize] [JSON:API Syntax Error] A data item does not contain type field!');
  }

  if (Array.isArray(response.data)) {
    // Getting the type of the first element
    const type = response.data[0].type;
    for (const item of response.data) {
      // Checking if an data item has type field
      if (!item.hasOwnProperty('type')) {
        throw new Error(
          '[Normalize] [JSON:API Syntax Error] A data item does not contain type field!'
        );
      }
      // Checking if the current item has the same type as the first
      if (item.type !== type) {
        throw new Error(
          '[Normalize] [JSON:API Syntax Error] Not all data items are the same type!'
        );
      }
    }
  }
  return false;
}

// Creating interface for the result object
export interface Result {
  [key: string]: Record<string, any>[];
}

export const normalizeResource = <T>(
  response: JSONApi.Response<T>,
  resource: JSONApi.Resource<T> | null,
  visited = new Set()
): Record<string, any> | null => {
  if (!resource) {
    return null;
  }

  const { id, type, attributes, meta } = resource;

  // Create a unique identifier for the current resource
  const resourceIdentifier = `${type}:${id}`;

  // If the resource has already been visited, return null to prevent infinite recursion
  if (visited.has(resourceIdentifier)) {
    return null;
  }

  // Mark the current resource as visited
  visited.add(resourceIdentifier);

  const relationships = Object.entries(resource.relationships ?? {}).reduce(
    (acc, [name, relationship]) => {
      return {
        ...acc,
        [name]: Array.isArray(relationship.data)
          ? relationship.data.map((rel) =>
              normalizeResource(response, rel ? findItem(response, resource, rel) : null, visited)
            )
          : normalizeResource(
              response,
              relationship.data ? findItem(response, resource, relationship.data) : null,
              visited
            ),
      };
    },
    { id, type } as Record<string, any>
  );
  return { id, type, ...attributes, ...relationships, ...meta };
};
/**
 * Normalizing a JSON:API styled server response
 *
 */
export const normalize = <T>(response: JSONApi.Response<T>) => {
  // Validating data field from the server response
  if (validate(response)) {
    return Array.isArray(response.data) ? [] : {};
  }

  const visited = new Set();

  if (Array.isArray(response.data)) {
    return response.data.map((resource) => {
      return normalizeResource(response, resource, visited);
    });
  } else {
    return normalizeResource(response, response.data, visited);
  }
};

export default normalize;
